import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import { useAuthStoreRefactor } from "./authStoreRefactor";
import cable, { createAuthenticatedConsumer } from "../services/cable";

// Create base store WITHOUT ActionCable (we'll handle it manually)
const notificationsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/notifications",
  name: "NotificationsStoreRefactor",
  objectName: "Notificatie",
  enableCache: false,
  actionCableChannel: "NotificationChannel", // ← Enable base store ActionCable
  infiniteLoading: false,
});

export const useNotificationsStoreRefactor = defineStore(
  "notificationsRefactor",
  {
    ...notificationsBaseStore,

    getters: {
      ...notificationsBaseStore.getters,

      /**
       * Get all notifications (maps to state.data for compatibility)
       */
      notifications: (state) => {
        return state.data || [];
      },

      /**
       * Backward compatibility - same as notifications
       */
      getNotifications: (state) => {
        return state.data || [];
      },

      /**
       * Get notification by ID
       */
      getNotificationById: (state) => (id) => {
        return state.data.find((notification) => notification.id == id);
      },

      /**
       * Get unread notifications
       */
      getUnreadNotifications: (state) => {
        return state.data.filter((notification) => !notification.read);
      },

      /**
       * Get unread count
       */
      getUnreadCount: (state) => {
        return state.data.filter((notification) => !notification.read).length;
      },

      /**
       * Get read notifications
       */
      getReadNotifications: (state) => {
        return state.data.filter((notification) => notification.read);
      },

      /**
       * Get notifications by type
       */
      getNotificationsByType: (state) => (type) => {
        return state.data.filter(
          (notification) => notification.notification_type === type,
        );
      },
    },

    actions: {
      ...notificationsBaseStore.actions,

      async init() {
        if (this._initialized) return;
        this._initialized = true;
        this.subscribeToActionCable(); // Uses base store implementation
      },

      unsubscribeFromActionCable() {
        if (this.subscription) {
          try {
            console.log(
              `NotificationsStore: 🔌 Unsubscribing from NotificationChannel`,
            );
            this.subscription.unsubscribe();
          } catch (error) {
            console.error(`NotificationsStore: Error unsubscribing:`, error);
          } finally {
            this.subscription = null;
            this.subscribed = false;

            // Update debug store
            const debugStore = useActionCableDebugStore();
            debugStore.updateConnectionStatus(
              "NotificationsStoreRefactor",
              false,
            );
          }
        }
      },

      /**
       * Mark all notifications as read
       */
      async readAll() {
        try {
          this.loading = true;
          await this.customApiCall(
            "PATCH",
            "/api/v1/notifications/read_all",
            {},
            "Alle notificaties zijn als gelezen gemarkeerd",
          );

          // Update all notifications in the state
          this.data = this.data.map((notification) => ({
            ...notification,
            read: true,
          }));

          return { success: true };
        } catch (error) {
          console.error(
            "NotificationsStoreRefactor: Error marking all notifications as read:",
            error,
          );
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Mark single notification as read
       */
      async read(notificationId) {
        try {
          this.loading = true;
          const result = await this.customApiCall(
            "PATCH",
            `/api/v1/notifications/${notificationId}/read`,
            null,
            "Notificatie gemarkeerd als gelezen",
          );

          // Update the notification in state
          const index = this.data.findIndex((n) => n.id === notificationId);
          if (index !== -1) {
            this.data[index] = {
              ...this.data[index],
              read: true,
            };
          }

          return result;
        } catch (error) {
          console.error(
            "NotificationsStoreRefactor: Error marking notification as read:",
            error,
          );
          throw error;
        } finally {
          this.loading = false;
        }
      },

      // All other methods including CRUD operations are fully inherited from base store
      // Auto-generated messages use "Notificatie" from objectName
      // ActionCable integration is handled automatically by base store
    },
  },
);
